import { pascalCase } from 'change-case'
import { FieldDefinition, ResourceSchemaMetadata } from '../api'
import { useApiClient } from './api-client'

export const useDataSchema = () => {
  const apiClient = useApiClient()

  const getFieldsForResource = async (
    objectName: string,
  ): Promise<FieldDefinition> => {
    const fieldDefinitions = await apiClient.dataSchema.fetchFieldDefinitions()
    return fieldDefinitions?.[pascalCase(objectName)]
  }

  const getDefinerForResource = async (objectName: string): Promise<string> => {
    const fieldDefinition = await getFieldsForResource(objectName)
    return fieldDefinition?.definer ?? 'UID'
  }

  const getSearchFieldsForResource = async (
    objectName: string,
  ): Promise<string[]> => {
    const fieldDefinition = await getFieldsForResource(objectName)
    return fieldDefinition?.searchByFields ?? ['UID']
  }

  /**
   * Effective schema for a resource — every field with its ElasticORM
   * `type`, nullability, picklist values and lookup relationship. Resolves
   * the resource name to its schema mapping first (IndexedDB-cached), then
   * fetches `custom/metadata/:mapping`.
   */
  const getSchemaMetadataForResource = async (
    objectName: string,
  ): Promise<ResourceSchemaMetadata> => {
    const schemas = await apiClient.dataSchema.getSchemaObjects()
    const match = schemas.find(
      (schema) => schema.name.toLowerCase() === objectName.toLowerCase(),
    )
    // Fall back to the input as the mapping when the catalogue doesn't
    // enumerate it — keeps the hook honest for the common name===mapping
    // case and resources the catalogue omits. A genuinely unknown resource
    // surfaces as a 404 from `custom/metadata/:mapping` (the caller's
    // error path), rather than a name-resolution error here.
    return apiClient.dataSchema.getSchemaFields(match?.mapping ?? objectName)
  }

  return {
    getFieldsForResource,
    getDefinerForResource,
    getSearchFieldsForResource,
    getSchemaMetadataForResource,
  }
}
